import { useEffect, useRef, useState } from 'react';
import { ArrowDownLeft, ArrowUpRight, Check, List, Loader2, Pencil, Tag, X } from 'lucide-react';
import { useRecentTransactions } from '@/hooks/useFinancialData';
import { useCategories } from '@/hooks/useCategories';
import { formatBRL, formatDate } from '@/lib/format';
import { SectionHeader } from '@/components/ui/SectionHeader';
import { EmptyState } from '@/components/ui/EmptyState';
import { cn } from '@/lib/utils';

// ─── Category Picker inline ───────────────────────────────────────────────────

interface PickerProps {
  transactionId: string;
  description: string;
  direction: 'in' | 'out';
  currentCategoryId?: string;
  onSaved: () => void;
  onClose: () => void;
}

function CategoryPicker({ transactionId, description, direction, currentCategoryId, onSaved, onClose }: PickerProps) {
  const { categories, ensureCategory, applyCategory } = useCategories();
  const [selected, setSelected] = useState(currentCategoryId ?? '');
  const [newName, setNewName] = useState('');
  const [applyToAll, setApplyToAll] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const ref = useRef<HTMLDivElement>(null);

  // Fecha ao clicar fora
  useEffect(() => {
    function onClickOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose();
    }
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, [onClose]);

  const defaultKind = direction === 'in' ? 'revenue' : 'expense';
  const filtered = categories.filter((c) => c.kind === defaultKind);

  async function handleSave() {
    setSaving(true);
    setError('');
    try {
      let catId = selected;
      if (!catId && newName.trim()) {
        catId = await ensureCategory(newName.trim(), defaultKind);
      }
      if (!catId) { setError('Selecione ou crie uma categoria.'); setSaving(false); return; }
      await applyCategory(transactionId, description, catId, applyToAll);
      onSaved();
    } catch (e: any) {
      setError(e.message ?? 'Erro ao salvar');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div
      ref={ref}
      className="absolute z-50 right-0 top-8 w-72 card p-4 shadow-2xl border border-border"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="flex items-center justify-between mb-3">
        <p className="text-xs font-semibold text-ink flex items-center gap-1.5">
          <Tag className="w-3.5 h-3.5 text-brand-700" />
          Classificar transação
        </p>
        <button onClick={onClose} className="text-ink-muted hover:text-ink">
          <X className="w-4 h-4" />
        </button>
      </div>

      <p className="text-xs text-ink-muted mb-3 truncate" title={description}>{description}</p>

      {/* Categorias existentes */}
      <div className="space-y-1 mb-3 max-h-36 overflow-y-auto">
        {filtered.length === 0 && !newName && (
          <p className="text-xs text-ink-soft">Nenhuma categoria ainda. Crie abaixo.</p>
        )}
        {filtered.map((c) => (
          <button
            key={c.id}
            onClick={() => { setSelected(c.id); setNewName(''); }}
            className={cn(
              'w-full text-left text-sm px-3 py-2 rounded-lg transition-colors flex items-center justify-between',
              selected === c.id
                ? 'bg-brand-50 text-brand-800 font-medium'
                : 'hover:bg-surface-subtle text-ink'
            )}
          >
            {c.name}
            {selected === c.id && <Check className="w-3.5 h-3.5" />}
          </button>
        ))}
      </div>

      {/* Nova categoria */}
      <input
        type="text"
        className="input w-full text-sm mb-3"
        placeholder="+ Nova categoria…"
        value={newName}
        onChange={(e) => { setNewName(e.target.value); if (e.target.value) setSelected(''); }}
      />

      {/* Aplicar a todas */}
      <label className="flex items-start gap-2 cursor-pointer mb-3">
        <input
          type="checkbox"
          className="mt-0.5 rounded"
          checked={applyToAll}
          onChange={(e) => setApplyToAll(e.target.checked)}
        />
        <span className="text-xs text-ink-muted">
          Aplicar a <strong>todas</strong> as transações com o mesmo destinatário e salvar como regra
        </span>
      </label>

      {error && <p className="text-xs text-negative mb-2">{error}</p>}

      <button
        onClick={handleSave}
        disabled={saving || (!selected && !newName.trim())}
        className="btn-primary w-full gap-2 !py-2"
      >
        {saving && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
        Salvar
      </button>
    </div>
  );
}

// ─── Tabela principal ─────────────────────────────────────────────────────────

export function RecentTransactions({ dateRange }: { dateRange?: { from: Date; to: Date } }) {
  const { data, loading, refetch } = useRecentTransactions(100, dateRange?.from, dateRange?.to);
  const { categories } = useCategories();
  const [editingId, setEditingId] = useState<string | null>(null);

  const catMap = new Map(categories.map((c) => [c.id, c.name]));

  return (
    <div className="card p-5">
      <SectionHeader
        title="Transações recentes"
        subtitle={loading ? 'Carregando…' : `${data.length} mais recentes · clique no lápis para classificar`}
      />

      {loading ? (
        <div className="space-y-2 mt-2">
          {[1,2,3,4,5].map((i) => (
            <div key={i} className="h-12 rounded-xl bg-surface-subtle animate-pulse" />
          ))}
        </div>
      ) : data.length === 0 ? (
        <EmptyState
          icon={List}
          title="Nenhuma transação importada"
          description="Importe seu extrato CSV para visualizar as movimentações aqui."
        />
      ) : (
        <div className="overflow-x-auto -mx-5">
          <table className="w-full text-sm min-w-[640px]">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wider text-ink-muted border-y border-border bg-surface-subtle/50">
                <th className="px-5 py-2 font-medium">Data</th>
                <th className="px-3 py-2 font-medium">Descrição</th>
                <th className="px-3 py-2 font-medium">Categoria</th>
                <th className="px-5 py-2 font-medium text-right">Valor</th>
              </tr>
            </thead>
            <tbody>
              {data.map((t) => {
                const isIn = t.direction === 'in';
                const catName = t.category_id ? catMap.get(t.category_id) : null;
                const isEditing = editingId === t.id;

                return (
                  <tr
                    key={t.id}
                    className="border-b border-border last:border-0 hover:bg-surface-subtle/40 transition-colors"
                  >
                    {/* Data */}
                    <td className="px-5 py-2.5 text-ink-muted text-xs whitespace-nowrap">
                      {formatDate(t.occurred_at)}
                    </td>

                    {/* Descrição */}
                    <td className="px-3 py-2.5 max-w-[280px]">
                      <div className="flex items-center gap-2">
                        <span className={cn(
                          'w-6 h-6 rounded-lg flex items-center justify-center shrink-0',
                          isIn ? 'bg-positive/10 text-positive' : 'bg-negative/10 text-negative'
                        )}>
                          {isIn
                            ? <ArrowDownLeft className="w-3.5 h-3.5" />
                            : <ArrowUpRight className="w-3.5 h-3.5" />}
                        </span>
                        <span className="text-ink truncate">{t.description}</span>
                      </div>
                    </td>

                    {/* Categoria + botão editar */}
                    <td className="px-3 py-2.5">
                      <div className="relative flex items-center gap-2">
                        {catName ? (
                          <span className="chip bg-brand-50 text-brand-800 text-[11px]">
                            {catName}
                          </span>
                        ) : (
                          <span className="text-xs text-ink-soft italic">sem categoria</span>
                        )}
                        <button
                          onClick={() => setEditingId(isEditing ? null : t.id)}
                          className="text-ink-muted hover:text-brand-700 transition-colors shrink-0"
                          title="Classificar transação"
                        >
                          <Pencil className="w-3.5 h-3.5" />
                        </button>

                        {isEditing && (
                          <CategoryPicker
                            transactionId={t.id}
                            description={t.description}
                            direction={t.direction}
                            currentCategoryId={t.category_id}
                            onSaved={() => { setEditingId(null); refetch(); }}
                            onClose={() => setEditingId(null)}
                          />
                        )}
                      </div>
                    </td>

                    {/* Valor */}
                    <td className={cn(
                      'px-5 py-2.5 text-right tabular-nums font-semibold whitespace-nowrap',
                      isIn ? 'text-positive' : 'text-negative'
                    )}>
                      {isIn ? '+' : '−'}{formatBRL(t.amount_cents)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
